import { FramedIcon } from '@heidi/design-system/framed-icon';
import { focusRing } from '@heidi/design-system/styles';
import { TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import { useCallback, type KeyboardEvent } from 'react';
import { useIntl } from 'react-intl';

export type RoutineOutcomeAction = 'created' | 'updated' | 'running' | 'ran';

export interface RoutineOutcomeRowProps {
  action: RoutineOutcomeAction;
  name: string;
  Icon: LucideIcon;
  /** Optional right-side meta, e.g. a schedule label. */
  meta?: string;
  onOpen?: () => void;
  className?: string;
  'data-testid'?: string;
  nameTestId?: string;
}

export const RoutineOutcomeRow = ({
  action,
  name,
  Icon,
  meta,
  onOpen,
  className,
  'data-testid': dataTestId = 'workflow-created-card',
  nameTestId,
}: RoutineOutcomeRowProps) => {
  const intl = useIntl();
  const canOpen = typeof onOpen === 'function';
  const actionLabel =
    action === 'created'
      ? intl.formatMessage({
          defaultMessage: 'Created routine',
          id: 'dj88ovV64s',
          description: 'Outcome row action after a routine is created from chat',
        })
      : action === 'updated'
        ? intl.formatMessage({
            defaultMessage: 'Updated routine',
            id: 'bjL2ANJ+j2',
            description: 'Outcome row action after a routine is updated from chat',
          })
        : action === 'running'
          ? intl.formatMessage({
              defaultMessage: 'Running routine',
              id: 'mAbDild/bS',
              description: 'Routine row action while the routine is running in chat',
            })
          : intl.formatMessage({
              defaultMessage: 'Ran routine',
              id: '0hc31KSebs',
              description: 'Outcome row action when a routine is run in chat',
            });

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!canOpen) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onOpen?.();
      }
    },
    [canOpen, onOpen]
  );

  return (
    <div
      className={cn(
        'relative flex h-14 w-full items-center gap-3 overflow-hidden rounded-xl border border-border bg-transparent p-3',
        canOpen && 'cursor-pointer hover:border-border-hover hover:bg-transparent',
        canOpen && focusRing,
        className
      )}
      data-testid={dataTestId}
      role={canOpen ? 'button' : undefined}
      tabIndex={canOpen ? 0 : undefined}
      onClick={canOpen ? onOpen : undefined}
      onKeyDown={canOpen ? handleKeyDown : undefined}
    >
      <FramedIcon size="sm" state="neutral" icon={<Icon />} />
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <TypographyP2 className="min-w-0 flex-1 text-sm font-medium text-text-primary">
          <span>{actionLabel}</span>
          <span>:</span>{' '}
          <span className="font-normal" data-testid={nameTestId}>
            {name}
          </span>
        </TypographyP2>
        {meta ? (
          <TypographyP2 className="shrink-0 text-text-tertiary">{meta}</TypographyP2>
        ) : null}
      </div>
      {canOpen ? (
        <ChevronRight
          className="size-4 shrink-0 text-text-secondary rtl:-scale-x-100"
          aria-hidden
          data-testid="routine-outcome-chevron"
        />
      ) : null}
    </div>
  );
};
